<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <!-- 登录页独立样式根：zone-login，见 src/styles/zone-login.css -->
  <div class="vben-login-page zone-login">
    <div class="vben-login-left">
      <div class="vben-brand">
        <BrandLogoMark
          v-if="branding.useLogoImage"
          :logo-url="branding.logoUrl"
          :logo-caption="branding.logoCaption"
          :logo-alt="branding.logoAlt"
          variant="md"
          on-dark
        />
        <div class="vben-brand-text">
          <span class="vben-brand-name">{{ branding.productName }}</span>
          <span class="vben-brand-slogan">{{ branding.slogan }}</span>
        </div>
      </div>
      <!-- 中间产品图（目标站风格动图）+ 下方产品说明 -->
      <div class="vben-slogan-wrap">
        <div class="vben-slogan-image">
          <img
            v-if="sloganImageUrl"
            :src="sloganImageUrl"
            alt="产品示意"
            class="vben-slogan-img"
          />
          <div v-else class="vben-slogan-placeholder">
            <span>数据驾驶舱</span>
          </div>
        </div>
        <p class="vben-page-title">懂你的股东智能决策系统</p>
        <p class="vben-page-desc">数据报告 · 股权穿透 · 筹码成本 · 交易趋势 · 股东档案</p>
      </div>
    </div>

    <div class="vben-login-right">
      <!-- 登录区 -->
      <div class="vben-login-form-wrap">
        <h1 class="vben-welcome">
          欢迎回来 <span class="vben-welcome-emoji">👋</span>
        </h1>
        <p class="vben-welcome-desc">请输入您的帐户信息以开始使用</p>

        <el-form
          :model="form"
          :rules="rules"
          ref="formRef"
          label-position="top"
          class="vben-form"
          @keyup.enter="handleSubmit"
        >
          <el-form-item label="用户名" prop="username">
            <el-input
              v-model="form.username"
              placeholder="请输入用户名"
              autocomplete="username"
              class="vben-input-full"
            />
          </el-form-item>
          <el-form-item label="密码" prop="password">
            <el-input
              v-model="form.password"
              type="password"
              placeholder="请输入密码"
              show-password
              autocomplete="current-password"
              class="vben-input-full"
            />
          </el-form-item>
          <div class="vben-form-options">
            <el-checkbox v-model="form.remember">记住用户名和密码</el-checkbox>
            <a href="javascript:;" class="vben-link" @click.prevent="onForgetPassword">忘记密码?</a>
          </div>
          <el-form-item>
            <el-button
              type="primary"
              :loading="loading"
              class="vben-btn-login"
              @click="handleSubmit"
            >
              {{ loading ? '登录中...' : '登录' }}
            </el-button>
          </el-form-item>
        </el-form>
      </div>

      <footer class="vben-footer">
        <div>Copyright © {{ new Date().getFullYear() }} {{ branding.copyrightName }}</div>
        <div class="vben-footer-icp">京ICP备2026015373号</div>
      </footer>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import axios from 'axios'
import '@/styles/zone-login.css'
import '@/styles/brand-logo-mark.css'
import {
  getStoredRemember,
  getStoredLoginUsername,
  getStoredLoginPassword,
  persistLoginRemember,
} from '@/utils/loginRemember.js'
import { resolveBrandingFromWindow } from '@/config/tenantBranding.js'
import BrandLogoMark from '@/components/BrandLogoMark.vue'

const router = useRouter()
const branding = resolveBrandingFromWindow()
const formRef = ref(null)
const form = reactive({
  username: getStoredLoginUsername(),
  password: '',
  remember: getStoredRemember()
})

const rules = {
  username: [{ required: true, message: '请输入用户名', trigger: 'blur' }],
  password: [{ required: true, message: '请输入密码', trigger: 'blur' }]
}

const loading = ref(false)
// 中间产品图：备选图 B（unDraw Data reports）已保存为 public/images/login-slogan.svg
const sloganImageUrl = ref('/images/login-slogan.svg')

onMounted(() => {
  form.username = getStoredLoginUsername()
  form.remember = getStoredRemember()
  if (form.remember) {
    form.password = getStoredLoginPassword()
  }
})

// 忘记密码：占位，后续对接
function onForgetPassword() {
  ElMessage.info('忘记密码功能敬请期待')
}

const handleSubmit = () => {
  if (loading.value) return
  if (!formRef.value) return
  formRef.value.validate(async (valid) => {
    if (!valid) return
    loading.value = true
    try {
      const res = await axios.post('/api/auth/login/', {
        username: form.username,
        password: form.password
      })
      if (res.data.status === 'ok') {
        const user = res.data.data || {}
        localStorage.setItem('currentUser', JSON.stringify(user))
        persistLoginRemember(form.username, form.password, form.remember)
        ElMessage.success('登录成功')
        try {
          await router.replace('/')
        } catch (navErr) {
          console.error(navErr)
          window.location.assign('/')
        }
      } else {
        ElMessage.error(res.data.message || '登录失败')
      }
    } catch (e) {
      const status = e.response?.status
      // 仅 5xx 或网络异常跳维护页；用户名/密码错误（401 等 4xx）不跳转，正常提示
      const isServerError = status >= 500 || status === 502 || status === 503 || status === 504
      const isNetworkError = !e.response
      if (isServerError || isNetworkError) {
        console.error('[登录失败-跳维护页]', e.response?.status, e.response?.data, e.message, e)
        router.push('/maintenance')
        return
      }
      ElMessage.error(e.response?.data?.message || e.message || '登录失败')
    } finally {
      loading.value = false
    }
  })
}
</script>
